import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardContent } from "../../Components/UI/card";
import { Input } from "../../Components/UI/input";
import { Button } from "../../Components/UI/button";
import {
  ChevronRight,
  ArrowLeft,
  History,
  Target,
  AlertTriangle,
  Smile,
  Clock,
  ShoppingCart,
  HeadphonesIcon,
  BarChart3,
  MapPin,
  MessageSquare,
  TrendingDown,
  TrendingUp,
  Frown,
  UserX,
  PieChart,
  Package,
  Users,
  MapPinIcon,
  Timer,
  Calendar,
  FileText,
  Search,
  X,
  RotateCcw,
} from "lucide-react";
import { getThreadNameFromTypeID, isMobileDevice } from "../../Utils/Utilities";
import { useNavigate, Link } from "react-router-dom";
import { updateHeaderConfig, checkIfInqOnlyMode, isSmartSellerMode } from "../../Store/Reducers";
import ComingSoon from "../../Components/Common/ComingSoon";
import { getActiveThreadTypesArr } from "../../Store/Reducers";
import useIsMobile from "../../Hooks/useIsMobile";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}
const Reports = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const [showComingSoon, setShowComingSoon] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const threadTypes = useSelector(getActiveThreadTypesArr);
  const threadTypesMaster = useSelector(
    (state) => state.masterData.threadTypes
  );
  const recordTypes = useSelector((state) => state.masterData.recordTypes);
  const isInqMode = useSelector(checkIfInqOnlyMode);

  const shouldShowThreadType = (tID) => {
    const thread = threadTypes.find((t) => t.TID === tID);
    return thread?.Sh === true;
  };

  const getThreadTypeName = (tID) => {
    return getThreadNameFromTypeID(tID, threadTypesMaster);
  };

  const getCurrentMonth = () => {
    return new Date().getMonth() + 1;
  };

  const getSubReportPath = (reportID) => {
    let route = "/";
    switch (reportID) {
      case "effortLog":
        route = "/effort-log";
        break;
      default:
        route = "/";
        break;
    }
    return route;
  };

  const getReportPath = (report, parentID) => {
    if (report.id === "visitLog") {
      const visitType = recordTypes?.find(
        (rt) => rt.Typ === "Visit" || rt.Typ === "Visit Log"
      );
      // Dynamically use visitType.TID instead of hardcoding
      return visitType ? `/records?iid=${visitType.TID}` : "/records";
    }
    if (report.id === "interactionHistory") {
      return "/records";
    }

    if (parentID === "monthlyRecords") {
      return report.tID
        ? `/threads-report?tID=${report.tID}&m=${getCurrentMonth()}`
        : null;
    }

    if (report.id === "effortLog") {
      return "/effort-log";
    }

    const customerAnalysisParams = [null, "?p=1", "?sm=h", "?sm=uh"];
    const routes = {
      monthlyRecords:
        report.tID &&
        `/threads-report?tID=${report.tID}&m=${getCurrentMonth()}`,
      teamPerformance: getSubReportPath(report.id),
      customerAnalysis:
        report.id === "productDemand"
          ? `/product-demand?m=${getCurrentMonth()}`
          : report.id === "biggestCustomers"
            ? "/biggest-customers"
            : null,
    };

    if (parentID === "analysisForReforms") {
      let url = `/loss-analysis?tid=${report.tID}`;
      if (report.rType) url += `&rType=${report.rType}`;
      return url;
    }

    if (parentID === "customerAnalysis") {
      if (report.id === "productDemand") {
        return `/product-demand?m=${getCurrentMonth()}`;
      }
      if (report.id === "biggestCustomers") {
        return `/customer-analysis?typeId=1`; // Biggest
      }
      if (report.id === "happiestCustomers") {
        return `/customer-analysis?typeId=2`; // Happy
      }
      if (report.id === "unhappyCustomers") {
        return `/customer-analysis?typeId=3`; // Unhappy
      }
      if (report.id === "segmentShare") {
        return `/segment-analysis`;
      }
      if (report.id === "lostCustomers") {
        return `/connections?na=1`;
      }
      if (report.id === "regionShare") {
        return `/region-analysis`;
      }
    }

    return null;
  };

  const getSubReports = (reportType) => {
    const baseSubReports = {
      monthlyRecords: [
        {
          id: "sInquiryHistory",
          title: getThreadTypeName(1) + " History",
          description:
            "Track monthly inquiry records and trends across all sales channels.",
          icon: History,
          show: shouldShowThreadType(1),
          tID: 1,
          tags: ["inquiry", "inquiries", "leads", "history", "monthly", "trends", "sales inquiries", "volume", "count", "month on month"],
        },
        {
          id: "sOrderHistory",
          title: isInqMode ? "Sales" : getThreadTypeName(2) + " History",
          description:
            "View monthly sales records and patterns for revenue analysis.",
          icon: ShoppingCart,
          show: shouldShowThreadType(2) || isInqMode,
          tID: 2,
          tags: ["sales", "orders", "order history", "revenue", "monthly", "sales trends", "patterns", "income", "order volume"],
        },
        {
          id: "supportHistory",
          title: getThreadTypeName(3) + " History",
          description:
            "Analyze monthly support ticket records and resolution trends.",
          icon: HeadphonesIcon,
          show: shouldShowThreadType(3),
          tID: 3,
          tags: ["support", "tickets", "service", "complaints", "helpdesk", "resolution", "monthly support", "issues"],
        },
        {
          id: "pInquiryHistory",
          title: getThreadTypeName(4) + " History",
          description:
            "Track monthly inquiry records and trends across all purchase channels.",
          icon: History,
          show: shouldShowThreadType(4),
          tID: 4,
          tags: ["purchase", "procurement", "vendor inquiry", "purchase inquiry", "monthly", "trends"],
        },
        {
          id: "pOrderHistory",
          title: getThreadTypeName(5) + " History",
          description:
            "View monthly sales records and patterns for procurement analysis.",
          icon: ShoppingCart,
          show: shouldShowThreadType(5),
          tID: 5,
          tags: ["purchase order", "procurement", "vendor order", "monthly", "patterns"],
        },
        {
          id: "complaintHistory",
          title: getThreadTypeName(6) + " History",
          description:
            "Analyze monthly supplier complaint records and resolution trends.",
          icon: HeadphonesIcon,
          show: shouldShowThreadType(6),
          tID: 6,
          tags: ["supplier complaint", "vendor complaint", "tickets", "resolution", "monthly"],
        },
      ],
      analysisForReforms: [
        {
          id: "inquiryFailure",
          title: isInqMode
            ? "Failure Analysis"
            : getThreadTypeName(1) + " Failure Analysis",
          description:
            "Analyze the root causes and trends behind inquiry failures.",
          icon: TrendingDown,
          show: shouldShowThreadType(1),
          tID: 1,
          rType: 1,
          tags: ["failure", "lost", "rejected", "inquiry loss", "loss reasons", "rejection", "drop", "deals lost", "reforms"],
        },
        {
          id: "orderFailure",
          title: getThreadTypeName(2) + " Failure Analysis",
          description: "Analyze the root causes and trends behind order failures.",
          icon: TrendingDown,
          show: !isInqMode && shouldShowThreadType(2),
          tID: 2,
          rType: 1,
          tags: ["order failure", "cancelled", "order loss", "rejection", "deals lost", "reforms", "drop"],
        },
        {
          id: "supportFailure",
          title: getThreadTypeName(3) + " Failure Analysis",
          description: "Analyze the root causes and trends behind support ticket failures.",
          icon: TrendingDown,
          show: !isInqMode && shouldShowThreadType(3),
          tID: 3,
          rType: 1,
          tags: ["support failure", "unresolved", "escalation", "ticket failure", "complaint loss", "reforms"],
        },
        {
          id: "inquiryDisqualification",
          title: isInqMode
            ? "Disqualification Analysis"
            : getThreadTypeName(1) + " Disqualification",
          description:
            "Analyze the reasons for disqualification and identify underlying trends.",
          icon: UserX,
          show: shouldShowThreadType(1),
          tID: 1,
          rType: 2,
          tags: ["disqualification", "junk", "invalid", "spam", "unqualified", "not eligible", "disqualify", "leads"],
        },
        {
          id: "orderDisqualification",
          title: getThreadTypeName(2) + " Disqualification",
          description: "Analyze the reasons for disqualification and identify underlying trends.",
          icon: UserX,
          show: !isInqMode && shouldShowThreadType(2),
          tID: 2,
          rType: 2,
          tags: ["order disqualification", "invalid orders", "disqualify", "unqualified"],
        },
        {
          id: "supportDisqualification",
          title: getThreadTypeName(3) + " Disqualification",
          description: "Analyze the reasons for disqualification and identify underlying trends.",
          icon: UserX,
          show: !isInqMode && shouldShowThreadType(3),
          tID: 3,
          rType: 2,
          tags: ["support disqualification", "invalid tickets", "disqualify"],
        },
      ],
      customerAnalysis: [
        {
          id: "biggestCustomers",
          title: "Biggest Customers",
          description:
            "Identify your highest value customers by revenue and order volume.",
          icon: TrendingUp,
          show: true,
          aID: 1,
          tags: ["biggest", "top customers", "high value", "vip", "revenue", "top buyers", "key accounts", "sales volume"],
        },
        {
          id: "happiestCustomers",
          title: "Happiest Customers",
          description:
            "Track most satisfied customers based on feedback and interactions.",
          icon: Smile,
          show: true,
          aID: 2,
          tags: ["happiest", "happy", "satisfied", "nps", "feedback", "ratings", "reviews", "positive", "loyal"],
        },
        {
          id: "unhappyCustomers",
          title: "Unhappy Customers",
          description:
            "Identify dissatisfied customers needing immediate attention.",
          icon: Frown,
          show: true,
          aID: 3,
          tags: ["unhappy", "dissatisfied", "bad feedback", "complaints", "churn risk", "negative", "at risk"],
        },
        {
          id: "lostCustomers",
          title: "Lost Customers",
          description:
            "Track customers with no repeat business for retention analysis.",
          icon: UserX,
          show: true,
          aID: 4,
          tags: ["lost customers", "inactive", "no repeat", "churn", "dormant", "retention", "attrition"],
        },
        {
          id: "distributions",
          title: "Distributions",
          description:
            "View customer and business distributions across various segments.",
          icon: PieChart,
          show: false,
          aID: 5,
          tags: ["distributions", "segments", "customer breakdown"],
        },
        {
          id: "productDemand",
          title: "Product Demand",
          description: "Analyze product demand for both inquiries and orders.",
          icon: Package,
          show: true,
          aID: 6,
          tags: ["product demand", "offerings", "items", "popular products", "catalog", "demand analysis", "sales by product"],
        },
        {
          id: "segmentShare",
          title: "Segment Share",
          description: "View market segment share specifically for orders.",
          icon: Users,
          show: true,
          aID: 7,
          tags: ["segment", "category share", "customer category", "market share", "industry", "classification"],
        },
        {
          id: "regionShare",
          title: "Region Share",
          description:
            "Analyze regional distribution from state to city level for orders.",
          icon: MapPinIcon,
          show: true,
          aID: 8,
          tags: ["region", "geography", "city", "state", "territory", "location", "area", "zone", "geographic distribution"],
        },
      ],
      teamPerformance: [
        {
          id: "teamComparison",
          title: "Team Comparison",
          description:
            "Compare revenue, count, intentions, first response, missed calls, LCR, hit rate.",
          icon: BarChart3,
          show: !isMobile && !isSmartSellerMode(),
          tags: ["team comparison", "leaderboard", "revenue", "hit rate", "conversion", "missed calls", "response time", "executive performance", "sales rep"],
        },
        {
          id: "effortLog",
          title: "Team Efforts Summary",
          description:
            "Track sales efforts, intentions, and rejection reasons across team members.",
          icon: Target,
          show: !isSmartSellerMode(),
          tags: ["team efforts", "effort summary", "activities", "intentions", "rejections", "calls", "meetings", "followups", "productivity"],
        },
        {
          id: "visitLog",
          title: "Visit Log",
          description:
            "View visit logs with integrated map for location tracking.",
          icon: MapPin,
          show: true,
          tags: ["visit log", "field visits", "location", "map", "gps", "check-in", "client meetings", "travel", "field sales"],
        },
        {
          id: "interactionHistory",
          title: "Team Efforts Log",
          description:
            "Analyze team interactions by day or period for performance insights.",
          icon: MessageSquare,
          show: !isSmartSellerMode(),
          tags: ["team efforts log", "interactions", "timeline", "daily logs", "notes", "call logs", "history", "daily activity"],
        },
      ],
    };

    return baseSubReports[reportType] || [];
  };

  const reportsConfig = useMemo(() => [
    {
      id: "monthlyRecords",
      title: "Monthly Records",
      description:
        "View Sales inquiry history, Sales order history, and Support ticket history records.",
      show: true,
      subReports: getSubReports("monthlyRecords"),
      className: "mt-3",
    },
    {
      id: "teamPerformance",
      title: isSmartSellerMode ? "Performance" : "Team Performance",
      description:
        "Analyze team comparison, effort log, visit log, and interaction history.",
      show: true,
      subReports: getSubReports("teamPerformance"),
    },
    {
      id: "analysisForReforms",
      title: "Analysis for Reforms",
      description:
        "Review inquiry loss, order delays, and support quality metrics for sales processes.",
      show:
        shouldShowThreadType(1) ||
        shouldShowThreadType(2) ||
        shouldShowThreadType(3),
      subReports: getSubReports("analysisForReforms"),
    },
    {
      id: "customerAnalysis",
      title: "Customer Analysis",
      description:
        "Examine customer segments, distributions, and regional data.",
      show: true,
      subReports: getSubReports("customerAnalysis"),
    },
  ], [threadTypes, threadTypesMaster, recordTypes, isInqMode, isMobile]);

  const query = (searchTerm || "").toLowerCase().trim();

  const isMatch = (item) => {
    if (!query) return true;
    const titleMatch = item.title?.toLowerCase().includes(query);
    const descMatch = item.description?.toLowerCase().includes(query);
    const tagsMatch = item.tags?.some(tag => tag.toLowerCase().includes(query) || query.includes(tag.toLowerCase()));
    return Boolean(titleMatch || descMatch || tagsMatch);
  };

  const filteredReportsConfig = useMemo(() => {
    return reportsConfig.map((category) => {
      if (!category.show) return null;

      const visibleSubReports = category.subReports?.filter((subReport) => {
        if (!subReport.show) return false;
        return isMatch(subReport);
      }) || [];

      if (visibleSubReports.length === 0) return null;

      return {
        ...category,
        subReports: visibleSubReports
      };
    }).filter(Boolean);
  }, [reportsConfig, query]);

  const setHeaderData = () => {
    let headerObj = {
      title: "Reports",
      iconLeft: {
        icon: ArrowLeft,
        onClick: () => navigate(-1),
      },
      actionsRight: [],
    };

    dispatch(updateHeaderConfig(headerObj));
  };

  useEffect(() => {
    setHeaderData();
  }, []);

  const getIconBackground = (reportID, parentID) => {
    if (parentID === "analysisForReforms") return "bg-sahi-red";
    if (
      parentID === "customerAnalysis" &&
      (reportID === "unhappyCustomers" || reportID === "lostCustomers")
    ) {
      return "bg-sahi-red";
    }
    return "bg-sahi-primary-color";
  };

  const renderMainReports = () => (
    <div className="bg-bg-grey flex flex-col min-h-[calc(100vh-100px)]">
      {!isMobile && (
        <div className="flex justify-between items-center px-1 my-1 mb-8">
          <h1 className="text-3xl font-bold text-text-color web-header-name">
            Reports
          </h1>
          <div className="flex items-center gap-3">
            <div className="relative w-72 lg:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <Input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-8 h-10 w-full bg-white border-gray-200 rounded-lg text-sm focus-visible:ring-sahi-primary-color shadow-2xs"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded-full"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Empty State when no items match search */}
      {query && filteredReportsConfig.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center py-6 px-4 text-center my-auto min-h-[50vh]">
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs py-10 px-8 max-w-md w-full flex flex-col items-center text-center">
            <div className="relative mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400">
                <Search className="w-7 h-7 stroke-[1.75]" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow-xs border border-gray-200 flex items-center justify-center text-gray-400">
                <X className="w-3.5 h-3.5 stroke-[2.5]" />
              </div>
            </div>
            <h3 className="text-base font-semibold text-text-color mb-1.5">No reports found</h3>
            <p className="text-sm text-zinc-500 max-w-xs leading-relaxed mb-5">
              We couldn't find any results for <span className="font-medium text-zinc-800 break-words">"{searchTerm}"</span>. Try searching with different keywords like inquiries, sales, or performance.
            </p>
            <Button
              onClick={() => setSearchTerm('')}
              className="cursor-pointer"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Clear search
            </Button>
          </div>
        </div>
      )}

      <div className={`w-full ${isMobile ? "space-y-6 mt-3" : "space-y-10"}`}>
        {filteredReportsConfig.map((category) => {
          const visibleSubReports = category.subReports || [];

          return (
            <div key={category.id} className="space-y-4">
              {visibleSubReports.length > 0 && (
                <div
                  className={`bg-bg-grey rounded-lg ${(isMobile && category?.className) || ""
                    }`}
                >
                  <div className="mb-1.5 px-1">
                    <h2
                      className={`${isMobile
                        ? "text-[13px] font-medium text-gray-500"
                        : "text-xl font-medium text-text-color pb-2 inline-block"
                        }`}
                    >
                      {category.title}
                    </h2>
                  </div>

                  <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 ${isMobile ? "gap-3" : "gap-5"}`}>
                    {visibleSubReports.map((report) => {
                      const SubIconComponent = report.icon;
                      const reportPath = getReportPath(report, category.id);
                      // Logic for Red vs Primary color (for the icon tint in mobile)
                      const isCritical =
                        getIconBackground(report.id, category.id) ===
                        "bg-sahi-red";

                      const CardContentWrapper = () => (
                        <Card
                          key={report.id}
                          className={cn(
                            isMobile
                              ? "cursor-pointer transition-all duration-200 border-none rounded-lg shadow-sm bg-white hover:shadow-md hover:-translate-y-0.5 h-full relative group min-h-[60px]"
                              : "sahi-card !shadow-[0_2px_6px_rgba(0,0,0,0.04)] !rounded-xl !pb-3 cursor-pointer h-full"
                          )}
                        >
                          <CardContent
                            className={cn(
                              isMobile ? "p-3" : "sahi-card-content !gap-5"
                            )}
                          >
                            {/* ---------------- MOBILE LAYOUT (Compact, Icon after Title) ---------------- */}
                            {isMobile ? (
                              <div className="flex flex-col justify-center h-full">
                                <div className="flex items-center justify-between gap-2">
                                  {/* Title + Icon Group */}
                                  <div className="flex items-center gap-2 min-w-0">
                                    <h3 className="font-bold text-text-color text-[14px] leading-tight truncate">
                                      {report.title}
                                    </h3>
                                    {/* Icon: Inline, same size as text (14px = h-3.5) */}
                                    <SubIconComponent
                                      className={cn(
                                        "h-3.5 w-3.5 flex-shrink-0 opacity-90",
                                        isCritical
                                          ? "text-sahi-red"
                                          : "text-sahi-primary-color"
                                      )}
                                    />
                                  </div>

                                  {/* Small Arrow for affordance */}
                                  <ChevronRight className="text-gray-300 h-3.5 w-3.5 flex-shrink-0" />
                                </div>

                                {/* Description */}
                                <p className="text-[11px] text-dropdown-grey leading-tight mt-1 line-clamp-1">
                                  {report.description}
                                </p>
                              </div>
                            ) : (
                              // ---------------- DESKTOP LAYOUT (Matches Settings Card Style) ----------------
                              <>
                                <div
                                  className={cn(
                                    "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 mb-1",
                                    getIconBackground(report.id, category.id)
                                  )}
                                >
                                  <SubIconComponent className="h-6 w-6 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-text-color text-lg mb-1 leading-tight">
                                    {report.title}
                                  </h3>
                                  <p className="text-zinc-500 text-sm leading-relaxed overflow-hidden text-ellipsis line-clamp-2">
                                    {report.description}
                                  </p>
                                </div>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      );

                      return reportPath ? (
                        <Link
                          key={report.id}
                          to={reportPath}
                          className="block h-full"
                          target={isMobile ? "_self" : "_blank"}
                        >
                          <CardContentWrapper />
                        </Link>
                      ) : (
                        <div
                          key={report.id}
                          onClick={() => setShowComingSoon(true)}
                          className="h-full"
                        >
                          <CardContentWrapper />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
  return (
    <>
      {renderMainReports()}
      <ComingSoon
        open={showComingSoon}
        onOpenChange={setShowComingSoon}
        onClose={() => setShowComingSoon(false)}
      />
    </>
  );
};
export default Reports;
